import { useEffect, useState } from "react";
import {
  Group,
  Container,
  Paper,
  Title,
  Anchor,
  Progress,
  Button,
  Table,
  NumberInput,
  Flex
} from "@mantine/core";

const getState = (timers) => {
  return timers.reduce(
    (rState, action) => {
      switch (action.action) {
        case "START_TIMER": {
          const latestStart = rState.latestStart
            ? action.time > rState.latestStart.time
              ? action
              : rState.latestStart
            : action;

          const timerRunning =
            !rState.latestStop || latestStart.time > rState.latestStop.time;

          const timeDeltaMinutes = millsToMins(Date.now() - latestStart.time);

          const fullIntervalMinutes =
            latestStart.workInterval + latestStart.restInterval;

          const numIntervals = Math.ceil(
            timeDeltaMinutes / fullIntervalMinutes
          );

          const intervals = Array(numIntervals)
            .fill(0)
            .map((_, i) => ({
              time: latestStart.time + i * minsToMills(fullIntervalMinutes),
              workInterval: latestStart.workInterval,
              restInterval: latestStart.restInterval
            }));

          return {
            ...rState,
            timerRunning,
            latestStart,
            intervals
          };
        }
        case "CANCEL_TIMER": {
          const latestStop = rState.latestStop
            ? action.time > rState.latestStop.time
              ? action
              : rState.latestStop
            : action;

          const timerRunning =
            rState.latestStart && latestStop.time < rState.latestStart;

          return {
            ...rState,
            timerRunning,
            latestStop
          };
        }
        default: {
          return rState;
        }
      }
    },
    { intervals: [], timerRunning: false }
  );
};

const formatTime = (time) =>
  new Date(time).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit"
  });

const minsToMills = (minutes) => minutes * 1000 * 60;
const millsToMins = (mills) => mills / 1000 / 60;

const DEFAULT_WORK_INTERVAL = 25;
const DEFAULT_REST_INTERVAL = 10;

export default function App() {
  const [workInterval, setWorkInterval] = useState(DEFAULT_WORK_INTERVAL);
  const [restInterval, setRestInterval] = useState(DEFAULT_REST_INTERVAL);
  const [time, setTime] = useState();

  const onStartTimer = () => {
    const timers = JSON.parse(localStorage.timers || "[]");
    const newTimer = {
      action: "START_TIMER",
      time: Date.now(),
      workInterval,
      restInterval
    };
    localStorage.timers = JSON.stringify(timers.concat(newTimer));
    setTime(Date.now());
  };

  const onClickCancelTimer = () => {
    const timers = JSON.parse(localStorage.timers || "[]");
    const newTimer = {
      action: "CANCEL_TIMER",
      time: Date.now()
    };
    localStorage.timers = JSON.stringify(timers.concat(newTimer));
    setTime(Date.now());
  };

  const onClickResetHistory = () => {
    localStorage.timers = "[]";
    setTime(Date.now());
  };

  const timers = JSON.parse(localStorage?.timers || "[]");

  const state = getState(timers);

  const latestInterval = state.intervals.length
    ? state.intervals.slice(-1)[0]
    : null;

  useEffect(() => {
    const interval = setInterval(() => {
      setTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const timers = JSON.parse(localStorage?.timers || "[]");
    if (timers.length < 1000) return;
    localStorage.timers = JSON.stringify([]);
  }, []);

  const totalInterval = latestInterval
    ? latestInterval.workInterval + latestInterval.restInterval
    : 0;

  const nextInterval = latestInterval
    ? latestInterval.time + minsToMills(totalInterval)
    : 0;

  const deltaToNextInterval = millsToMins(nextInterval - time);

  const percentage = (1 - deltaToNextInterval / totalInterval) * 100;

  return (
    <Container size="lg">
      <Paper withBorder m="xs" p="sm">
        <Title order={1} align="center" mt="xs" mb="lg">
          A Pomodoro Timer
        </Title>
        {state.timerRunning && latestInterval ? (
          <Paper withBorder shadow="md" p="xl">
            <Group position="apart" mb="sm">
              <Title
                variant="gradient"
                gradient={{ from: "red", to: "orange" }}
                order={3}
              >
                Started at {formatTime(latestInterval.time)}
              </Title>
              <Title
                variant="gradient"
                gradient={{ from: "red", to: "orange" }}
                order={3}
              >
                Current time {formatTime(time)}
              </Title>
              <Title
                variant="gradient"
                gradient={{ from: "red", to: "orange" }}
                order={3}
              >
                Ends at {formatTime(nextInterval)}
              </Title>
            </Group>
            <Progress
              color="orange"
              animate
              radius="xs"
              size="xl"
              value={percentage}
              label={`${Math.round(percentage)}%`}
              mb="sm"
            />
            <Group position="right">
              <Button
                mt="xs"
                variant="gradient"
                gradient={{ from: "red", to: "orange" }}
                onClick={onClickCancelTimer}
              >
                Cancel Timer
              </Button>
            </Group>
          </Paper>
        ) : (
          <Flex align="flex-end">
            <NumberInput
              defaultValue={DEFAULT_WORK_INTERVAL}
              value={workInterval}
              label="Work Interval (minutes)"
              onChange={setWorkInterval}
            />
            <NumberInput
              ml="xs"
              defaultValue={DEFAULT_REST_INTERVAL}
              value={restInterval}
              label="Rest Interval (minutes)"
              onChange={setRestInterval}
            />
            <Button ml="xs" onClick={onStartTimer} variant="gradient">
              Start Timer
            </Button>
          </Flex>
        )}
        <Paper withBorder shadow="md" mt="xl" mb="xl" p="xl">
          <Title variant="gradient" order={2} mb="xs" align="center">
            Interval History
          </Title>
          <Table>
            <thead>
              <tr>
                <th>Start Time</th>
                <th>Work Interval</th>
                <th>Rest Interval</th>
                <th>End Time</th>
              </tr>
            </thead>
            <tbody>
              {state.intervals.map((timer) => (
                <tr key={timer.time}>
                  <td>{formatTime(timer.time)}</td>
                  <td>{timer.workInterval} minutes</td>
                  <td>{timer.restInterval} minutes</td>
                  <td>
                    {formatTime(
                      timer.time +
                        minsToMills(timer.workInterval) +
                        minsToMills(timer.restInterval)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Group position="right">
            <Button mt="xs" onClick={onClickResetHistory} variant="gradient">
              Reset History
            </Button>
          </Group>
        </Paper>
        <Title order={5} align="center" mt="lg" mb="lg" color="dimmed">
          built by{" "}
          <Anchor href="https://andrueanderson.com" target="_blank">
            Andrue Anderson
          </Anchor>
        </Title>
      </Paper>
    </Container>
  );
}
